// components/NotificationPermissionModal.jsx
// 🔔 ENHANCED NOTIFICATION PERMISSION FLOW
// Pre-permission priming modal for 75-85% opt-in rates (vs 45% with raw browser prompt)
// v2: Added iOS Safari detection and "Add to Home Screen" instructions

import React, { useState, useEffect, useMemo } from 'react';
import { isNativeApp } from '../utils/isNativeApp';

/**
 * Detect if running on iOS
 */
const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

/**
 * Detect if running in Safari browser
 */
const isSafari = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/Chrome/.test(ua) && !/CriOS/.test(ua) && !/FxiOS/.test(ua);
};

/**
 * Detect if app is running as installed PWA (standalone mode)
 */
const isPWA = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches || 
    window.navigator.standalone === true;
};

/**
 * Check if push notifications are supported in current context
 */
const isPushSupported = () => {
  // iOS Safari (not PWA) doesn't support push
  if (isIOS() && !isPWA()) return false;
  
  // Check for notification API
  if (!('Notification' in window)) return false;
  
  // Check for service worker
  if (!('serviceWorker' in navigator)) return false;
  
  return true;
};

/**
 * Smart notification permission modal with:
 * - Pre-permission priming (explains value BEFORE browser prompt)
 * - iOS Safari detection with Add to Home Screen instructions
 * - Contextual triggers (appears at the right moment)
 * - Coin incentive option
 * - Graceful decline handling
 */
function NotificationPermissionModal({ 
  onClose, 
  onPermissionGranted,
  onPermissionDenied,
  triggerContext = 'default', // 'first_vote', 'created_trial', 'joined_crew', 'summons', 'default'
  coinReward = 100 // Coins awarded for enabling notifications
}) {
  // Detect platform on mount
  const platformInfo = useMemo(() => ({
    isNative: isNativeApp(),
    isIOS: isIOS(),
    isSafari: isSafari(),
    isPWA: isPWA(),
    isPushSupported: isPushSupported()
  }), []);

  useEffect(() => {
    if (!platformInfo.isNative) return;
    console.log('[pwa] pwa_prompt_suppressed_native', {
      platform: typeof window !== 'undefined' && window?.Capacitor?.getPlatform?.()
        ? window.Capacitor.getPlatform()
        : 'native',
      component: 'NotificationPermissionModal',
      trigger_context: triggerContext,
    });
    onClose?.();
  }, [platformInfo.isNative, onClose, triggerContext]);

  // Determine initial step based on platform
  const getInitialStep = () => {
    if (platformInfo.isIOS && !platformInfo.isPWA) {
      return 'ios_safari'; // Show Add to Home Screen instructions
    }
    return 'ask';
  };

  const [step, setStep] = useState(getInitialStep);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    setTimeout(() => setAnimateIn(true), 50);
  }, []);

  if (platformInfo.isNative) {
    return null;
  }

  // Get context-specific messaging
  const getContextMessage = () => {
    switch (triggerContext) {
      case 'first_vote':
        return {
          title: "🎉 Nice Vote!",
          subtitle: "Want to know when the verdict drops?",
          description: "Get notified when this trial ends and see if your side won!",
          benefits: [
            "⚖️ Verdict alerts when trials you voted on end",
            "📣 Know when friends summon you to vote",
            "🔥 Don't miss trending trials"
          ]
        };
      case 'created_trial':
        return {
          title: "🚀 Trial Created!",
          subtitle: "Stay updated on your trial",
          description: "Get notified as votes come in and when the verdict is final!",
          benefits: [
            "📊 See vote updates on your trials",
            "🏆 Get the final verdict instantly",
            "👥 Know when people share your trial"
          ]
        };
      case 'joined_crew':
        return {
          title: "👊 Welcome to the Crew!",
          subtitle: "Never miss a crew summons",
          description: "Your crewmates will summon you to vote on trials. Don't leave them hanging!",
          benefits: [
            "📣 Crew summons alerts",
            "⚖️ Vote together, win together",
            "🏆 Crew leaderboard updates"
          ]
        };
      case 'summons':
        return {
          title: "📣 Summons Sent!",
          subtitle: "Get summoned back",
          description: "When your friends summon you to vote, you'll want to know right away!",
          benefits: [
            "🔔 Instant summons notifications",
            "⚡ Never miss a vote",
            "🤝 Help your crew win"
          ]
        };
      default:
        return {
          title: "🔔 Stay in the Loop",
          subtitle: "Never miss a verdict",
          description: "Get notified about trials, verdicts, and when friends need your vote!",
          benefits: [
            "⚖️ Verdict alerts when trials end",
            "📣 Crew summons notifications",
            "🔥 Trending trial alerts"
          ]
        };
    }
  };

  const contextMessage = getContextMessage();

  const handleEnableNotifications = async () => {
    setStep('requesting');
    
    try {
      // Check if notifications are supported
      if (!('Notification' in window)) {
        setStep('blocked');
        return;
      }

      // Check current permission state
      if (Notification.permission === 'denied') {
        setStep('blocked');
        return;
      }

      // Request permission
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        setStep('success');
        
        // Callback to parent to handle FCM token registration
        if (onPermissionGranted) {
          onPermissionGranted();
        }
        
        // Auto-close after success animation
        setTimeout(() => {
          onClose();
        }, 2500);
      } else if (permission === 'denied') {
        setStep('denied');
        if (onPermissionDenied) {
          onPermissionDenied();
        }
      } else {
        // 'default' means they dismissed without choosing
        setStep('ask');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      setStep('denied');
    }
  };

  const handleMaybeLater = () => {
    // Store that they declined, so we can ask again later (but not too soon)
    localStorage.setItem('notification_declined_at', Date.now().toString());
    onClose();
  };

  const handleOpenSettings = () => {
    // Can't programmatically open settings, but we can guide them
    alert(
      'To enable notifications:\n\n' +
      '1. Click the lock/info icon in your browser\'s address bar\n' +
      '2. Find "Notifications" in the permissions\n' +
      '3. Change it to "Allow"\n' +
      '4. Refresh the page'
    );
  };

  return (
    <div style={{
      ...styles.overlay,
      opacity: animateIn ? 1 : 0,
      transition: 'opacity 0.3s ease-out'
    }} onClick={handleMaybeLater}>
      <div 
        style={{
          ...styles.modal,
          transform: animateIn ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.95)',
          opacity: animateIn ? 1 : 0,
          transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* iOS SAFARI STEP - Add to Home Screen instructions */}
        {step === 'ios_safari' && (
          <>
            <div style={styles.header}>
              <div style={styles.iconContainer}>
                <span style={styles.bellIcon}>📱</span>
                <div style={styles.iconPulse} />
              </div>
              <h2 style={styles.title}>One Quick Step!</h2>
              <p style={styles.subtitle}>Add GweffIT to your Home Screen</p>
            </div>

            <p style={styles.description}>
              To get notifications on iPhone, you need to install GweffIT as an app first. It only takes 10 seconds!
            </p>

            {/* iOS Instructions */}
            <div style={styles.iosInstructions}>
              <div style={styles.iosStep}>
                <div style={styles.iosStepNumber}>1</div>
                <div style={styles.iosStepContent}>
                  <div style={styles.iosStepTitle}>Tap the Share button</div>
                  <div style={styles.iosStepIcon}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2">
                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                      <polyline points="16 6 12 2 8 6"/>
                      <line x1="12" y1="2" x2="12" y2="15"/>
                    </svg>
                  </div>
                  <div style={styles.iosStepHint}>Look for this icon at the bottom of Safari</div>
                </div>
              </div>

              <div style={styles.iosStep}>
                <div style={styles.iosStepNumber}>2</div>
                <div style={styles.iosStepContent}>
                  <div style={styles.iosStepTitle}>Scroll and tap "Add to Home Screen"</div>
                  <div style={styles.iosStepEmoji}>➕🏠</div>
                  <div style={styles.iosStepHint}>You may need to scroll down in the share menu</div>
                </div>
              </div>

              <div style={styles.iosStep}>
                <div style={styles.iosStepNumber}>3</div>
                <div style={styles.iosStepContent}>
                  <div style={styles.iosStepTitle}>Tap "Add" in the top right</div>
                  <div style={styles.iosStepHint}>Then open GweffIT from your Home Screen</div>
                </div>
              </div>
            </div>

            {/* Coin incentive */}
            {coinReward > 0 && (
              <div style={styles.coinReward}>
                <span style={styles.coinIcon}>🪙</span>
                <span style={styles.coinText}>
                  +{coinReward} coins when you enable notifications!
                </span>
              </div>
            )}

            {/* Action buttons */}
            <div style={styles.buttonGroup}>
              <button 
                onClick={handleMaybeLater}
                style={styles.secondaryButton}
              >
                I'll do this later
              </button>
            </div>

            {/* Already installed hint */}
            <p style={styles.privacyNote}>
              Already installed? Open GweffIT from your Home Screen to enable notifications.
            </p>
          </>
        )}

        {/* ASK STEP - Pre-permission priming (non-iOS or PWA mode) */}
        {step === 'ask' && (
          <>
            {/* Header with icon */}
            <div style={styles.header}>
              <div style={styles.iconContainer}>
                <span style={styles.bellIcon}>🔔</span>
                <div style={styles.iconPulse} />
              </div>
              <h2 style={styles.title}>{contextMessage.title}</h2>
              <p style={styles.subtitle}>{contextMessage.subtitle}</p>
            </div>

            {/* Description */}
            <p style={styles.description}>{contextMessage.description}</p>

            {/* Benefits list */}
            <div style={styles.benefitsList}>
              {contextMessage.benefits.map((benefit, index) => (
                <div 
                  key={index} 
                  style={{
                    ...styles.benefitItem,
                    animationDelay: `${index * 0.1}s`
                  }}
                >
                  {benefit}
                </div>
              ))}
            </div>

            {/* Coin incentive */}
            {coinReward > 0 && (
              <div style={styles.coinReward}>
                <span style={styles.coinIcon}>🪙</span>
                <span style={styles.coinText}>
                  +{coinReward} coins for enabling notifications!
                </span>
              </div>
            )}

            {/* Action buttons */}
            <div style={styles.buttonGroup}>
              <button 
                onClick={handleEnableNotifications}
                style={styles.primaryButton}
              >
                <span style={styles.buttonIcon}>🔔</span>
                Enable Notifications
              </button>
              
              <button 
                onClick={handleMaybeLater}
                style={styles.secondaryButton}
              >
                Maybe Later
              </button>
            </div>

            {/* Privacy note */}
            <p style={styles.privacyNote}>
              We only send notifications you care about. No spam, ever.
            </p>
          </>
        )}

        {/* REQUESTING STEP - Waiting for browser prompt */}
        {step === 'requesting' && (
          <div style={styles.centerContent}>
            <div style={styles.spinner} />
            <h3 style={styles.loadingTitle}>Check your browser...</h3>
            <p style={styles.loadingText}>
              Click "Allow" in the browser prompt above
            </p>
          </div>
        )}

        {/* SUCCESS STEP */}
        {step === 'success' && (
          <div style={styles.centerContent}>
            <div style={styles.successIcon}>✅</div>
            <h3 style={styles.successTitle}>You're all set!</h3>
            <p style={styles.successText}>
              You'll never miss a verdict or summons now
            </p>
            {coinReward > 0 && (
              <div style={styles.coinAwarded}>
                <span style={styles.coinIconLarge}>🪙</span>
                <span style={styles.coinAwardedText}>+{coinReward} coins added!</span>
              </div>
            )}
          </div>
        )}

        {/* DENIED STEP */}
        {step === 'denied' && (
          <div style={styles.centerContent}>
            <div style={styles.deniedIcon}>😕</div>
            <h3 style={styles.deniedTitle}>Notifications Blocked</h3>
            <p style={styles.deniedText}>
              No worries! You can enable them later in settings.
            </p>
            <button 
              onClick={onClose}
              style={styles.primaryButton}
            >
              Got it
            </button>
          </div>
        )}

        {/* BLOCKED STEP - Previously denied, need manual fix */}
        {step === 'blocked' && (
          <div style={styles.centerContent}>
            <div style={styles.blockedIcon}>🔒</div>
            <h3 style={styles.blockedTitle}>Notifications are blocked</h3>
            <p style={styles.blockedText}>
              You previously blocked notifications. Here's how to fix it:
            </p>
            <button 
              onClick={handleOpenSettings}
              style={styles.primaryButton}
            >
              Show Me How
            </button>
            <button 
              onClick={onClose}
              style={{...styles.secondaryButton, marginTop: '12px'}}
            >
              Maybe Later
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper functions exported for use elsewhere
export const shouldShowNotificationPrompt = () => {
  // Don't show if already granted or denied
  if (typeof Notification !== 'undefined') {
    if (Notification.permission === 'granted') return false;
    if (Notification.permission === 'denied') return false;
  }
  
  // Don't show if declined recently (24 hour cooldown)
  const declinedAt = localStorage.getItem('notification_declined_at');
  if (declinedAt) {
    const hoursSinceDeclined = (Date.now() - parseInt(declinedAt)) / (1000 * 60 * 60);
    if (hoursSinceDeclined < 24) return false;
  }
  
  // Only show after user has engaged (at least 1 vote)
  const totalVotes = parseInt(localStorage.getItem('total_votes') || '0');
  if (totalVotes < 1) return false;
  
  return true;
};

export const markSignificantAction = (action) => {
  if (action === 'voted') {
    const current = parseInt(localStorage.getItem('total_votes') || '0');
    localStorage.setItem('total_votes', (current + 1).toString());
  }
};

export const getBestTriggerContext = () => {
  // Determine the best context based on recent user actions
  const lastAction = localStorage.getItem('last_significant_action');
  if (lastAction === 'created_trial') return 'created_trial';
  if (lastAction === 'joined_crew') return 'joined_crew';
  return 'first_vote';
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    backdropFilter: 'blur(8px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px'
  },
  modal: {
    backgroundColor: '#1a1a2e',
    borderRadius: '24px',
    maxWidth: '420px',
    width: '100%',
    padding: '32px',
    boxShadow: '0 25px 80px rgba(124, 58, 237, 0.4), 0 0 0 1px rgba(124, 58, 237, 0.2)',
    position: 'relative',
    overflow: 'hidden',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  header: {
    textAlign: 'center',
    marginBottom: '24px'
  },
  iconContainer: {
    position: 'relative',
    width: '80px',
    height: '80px',
    margin: '0 auto 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  bellIcon: {
    fontSize: '48px',
    position: 'relative',
    zIndex: 2,
    animation: 'bellRing 2s ease-in-out infinite'
  },
  iconPulse: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(124, 58, 237, 0.3) 0%, transparent 70%)',
    animation: 'pulse 2s ease-in-out infinite'
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '28px',
    fontWeight: '900',
    color: '#fff',
    letterSpacing: '-0.5px'
  },
  subtitle: {
    margin: 0,
    fontSize: '18px',
    color: '#a78bfa',
    fontWeight: '600'
  },
  description: {
    fontSize: '15px',
    color: '#ccc',
    lineHeight: '1.6',
    textAlign: 'center',
    marginBottom: '20px'
  },
  benefitsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '24px'
  },
  benefitItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    backgroundColor: 'rgba(124, 58, 237, 0.15)',
    borderRadius: '12px',
    fontSize: '15px',
    color: '#fff',
    fontWeight: '500',
    border: '1px solid rgba(124, 58, 237, 0.2)',
    animation: 'slideIn 0.4s ease-out backwards'
  },
  coinReward: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '16px',
    background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 165, 0, 0.1) 100%)',
    borderRadius: '12px',
    marginBottom: '24px',
    border: '1px solid rgba(255, 215, 0, 0.3)'
  },
  coinIcon: {
    fontSize: '24px'
  },
  coinText: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#ffd700'
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  primaryButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    width: '100%',
    padding: '18px 24px',
    background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '14px',
    fontSize: '17px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 8px 24px rgba(124, 58, 237, 0.4)',
    transition: 'all 0.2s ease'
  },
  buttonIcon: {
    fontSize: '20px'
  },
  secondaryButton: {
    width: '100%',
    padding: '14px 24px',
    background: 'transparent',
    color: '#999',
    border: '2px solid #333',
    borderRadius: '14px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  privacyNote: {
    textAlign: 'center',
    fontSize: '12px',
    color: '#666',
    marginTop: '16px',
    marginBottom: 0
  },
  centerContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 0',
    textAlign: 'center'
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid rgba(124, 58, 237, 0.2)',
    borderTopColor: '#7c3aed',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px'
  },
  loadingTitle: {
    margin: '0 0 8px 0',
    fontSize: '22px',
    fontWeight: '700',
    color: '#fff'
  },
  loadingText: {
    margin: 0,
    fontSize: '15px',
    color: '#999'
  },
  successIcon: {
    fontSize: '64px',
    marginBottom: '16px',
    animation: 'bounceIn 0.5s ease-out'
  },
  successTitle: {
    margin: '0 0 8px 0',
    fontSize: '24px',
    fontWeight: '700',
    color: '#22c55e'
  },
  successText: {
    margin: '0 0 20px 0',
    fontSize: '15px',
    color: '#ccc'
  },
  coinAwarded: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.3) 0%, rgba(255, 165, 0, 0.2) 100%)',
    borderRadius: '50px',
    animation: 'bounceIn 0.5s ease-out 0.3s backwards'
  },
  coinIconLarge: {
    fontSize: '28px'
  },
  coinAwardedText: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#ffd700'
  },
  deniedIcon: {
    fontSize: '64px',
    marginBottom: '16px'
  },
  deniedTitle: {
    margin: '0 0 8px 0',
    fontSize: '22px',
    fontWeight: '700',
    color: '#fff'
  },
  deniedText: {
    margin: '0 0 24px 0',
    fontSize: '15px',
    color: '#999'
  },
  blockedIcon: {
    fontSize: '64px',
    marginBottom: '16px'
  },
  blockedTitle: {
    margin: '0 0 8px 0',
    fontSize: '22px',
    fontWeight: '700',
    color: '#f59e0b'
  },
  blockedText: {
    margin: '0 0 24px 0',
    fontSize: '15px',
    color: '#999'
  },
  // iOS-specific styles
  iosInstructions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '24px'
  },
  iosStep: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '16px',
    backgroundColor: 'rgba(124, 58, 237, 0.1)',
    borderRadius: '16px',
    border: '1px solid rgba(124, 58, 237, 0.2)'
  },
  iosStepNumber: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '16px',
    flexShrink: 0
  },
  iosStepContent: {
    flex: 1
  },
  iosStepTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#fff',
    marginBottom: '4px'
  },
  iosStepIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    backgroundColor: 'rgba(124, 58, 237, 0.2)',
    borderRadius: '10px',
    margin: '8px 0'
  },
  iosStepEmoji: {
    fontSize: '28px',
    margin: '8px 0'
  },
  iosStepHint: {
    fontSize: '13px',
    color: '#888',
    lineHeight: '1.4'
  }
};

// Add CSS animations via style injection
if (typeof document !== 'undefined') {
  const styleId = 'notification-modal-animations';
  if (!document.getElementById(styleId)) {
    const styleSheet = document.createElement('style');
    styleSheet.id = styleId;
    styleSheet.textContent = `
      @keyframes bellRing {
        0%, 100% { transform: rotate(0deg); }
        10%, 30% { transform: rotate(15deg); }
        20% { transform: rotate(-15deg); }
        40% { transform: rotate(0deg); }
      }
      
      @keyframes pulse {
        0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
        50% { transform: translate(-50%, -50%) scale(1.3); opacity: 0; }
      }
      
      @keyframes slideIn {
        from { opacity: 0; transform: translateX(-20px); }
        to { opacity: 1; transform: translateX(0); }
      }
      
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      
      @keyframes bounceIn {
        0% { transform: scale(0); }
        50% { transform: scale(1.2); }
        100% { transform: scale(1); }
      }
    `;
    document.head.appendChild(styleSheet);
  }
}

export default NotificationPermissionModal;
